<template>
  <div class="restriktionen-mini card card-custom" @click="$emit('month-click', date)">
    <!-- Kopfzeile mit Monatsname und Anzahl gesperrter Tage -->
    <div class="restriktionen-mini__header d-flex align-items-center justify-content-between">
      <span class="restriktionen-mini__title">{{ monthName }}</span>
      <span v-if="blockedCount > 0" class="restriktionen-mini__count">{{ blockedCount }} gesperrt</span>
    </div>

    <!-- Wochentage -->
    <div class="restriktionen-mini__weekdays">
      <span v-for="weekday in weekdays" :key="weekday">{{ weekday }}</span>
    </div>

    <!-- Kompaktes Tagesraster -->
    <div class="restriktionen-mini__grid">
      <template v-for="(week, weekIndex) in weeks">
        <span
          v-for="day in week"
          :key="`${weekIndex}-${day.key}`"
          class="restriktionen-mini__day"
          :class="{ 'restriktionen-mini__day--outside': !day.inCurrentMonth }"
          :style="dayStyle(day)"
        >
          {{ day.dayNumber }}
        </span>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import {
  buildMonthMatrix,
  groupRestriktionenByDay,
  weekdaysShort,
  monthNames,
  countBlockedDays,
} from './restriktionen-calendar.helper.js';
import { getRestriktionTypeConfig, sortRestriktionenByPriority } from './restriktionen.constants.js';

export default {
  name: 'RestriktionenMiniMonthComponent',
  props: {
    date: {
      type: [Object, String, Date],
      required: true,
    },
    restriktionen: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    weekdays() {
      return weekdaysShort;
    },
    weeks() {
      return buildMonthMatrix(this.date);
    },
    monthName() {
      return monthNames[moment(this.date).month()];
    },
    restriktionenByDay() {
      return groupRestriktionenByDay(this.restriktionen);
    },
    blockedCount() {
      return countBlockedDays(this.restriktionen, this.date, 'month');
    },
  },
  methods: {
    // die Farbe eines Tages folgt der Restriktion mit der höchsten Priorität, wie in der Monatsansicht
    dayStyle(day) {
      if (!day.inCurrentMonth) {
        return {};
      }
      const entries = this.restriktionenByDay[day.key];
      if (!entries || !entries.length) {
        return {};
      }
      const config = getRestriktionTypeConfig(sortRestriktionenByPriority(entries)[0].type);
      return {
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 600,
      };
    },
  },
};
</script>

<style scoped>
.restriktionen-mini {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.restriktionen-mini:hover {
  border-color: var(--border-strong);
  box-shadow: var(--shadow-lg);
}

.restriktionen-mini__header {
  margin-bottom: 24px;
}

.restriktionen-mini__title {
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  color: var(--text);
}

.restriktionen-mini__count {
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  background-color: #f64e6026;
  color: #c10007;
  font-size: 9px;
  line-height: 12px;
  font-weight: 600;
}

.restriktionen-mini__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}

.restriktionen-mini__weekdays span {
  text-align: center;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  color: var(--text-muted);
}

.restriktionen-mini__grid {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  gap: 8px;
  min-height: 0;
}

.restriktionen-mini__day {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 0;
  border-radius: var(--radius-sm);
  font-size: 10px;
  line-height: 14px;
  color: var(--text-body);
}

.restriktionen-mini__day--outside {
  color: #80808f80;
}
</style>
